import {
  Box,
  HStack,
  Icon,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { format, isValid } from "date-fns";
import { Minus, Plus } from "lucide-react";
import type { EntitlementChange } from "../reviewLogic";

/** "12 Jun 2026" or null for a missing/invalid date. */
const fmtCorrectedAt = (value?: string): string | null => {
  if (!value) return null;
  const d = new Date(value);
  return isValid(d) ? format(d, "d MMM yyyy") : null;
};

/** "Corrected by Alex Lee · 12 Jun 2026" from whichever parts are present. */
const attribution = (change: EntitlementChange): string | null => {
  const when = fmtCorrectedAt(change.correctedAt);
  const who = change.correctedBy?.trim();
  const parts = [who ? `Corrected by ${who}` : null, when].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
};

/**
 * ChangeCard — a single recorded fix in the "Fix incorrect data" panel.
 * Corrections auto-save (PLO-4803), so cards are read-only: they show the
 * account plus the entitlements added / removed, with an "Applied" badge when
 * the change has been persisted.
 */

const EntTag = ({
  name,
  direction,
}: {
  name: string;
  direction: "added" | "removed";
}) => {
  const added = direction === "added";
  return (
    <Box
      display="inline-flex"
      alignItems="center"
      gap="1xs"
      px="sm"
      py="2px"
      borderRadius="component.md"
      border="1px"
      borderColor={added ? "success.300" : "error.300"}
      bg={added ? "bg.success" : "bg.error"}
      color={added ? "success.700" : "error.700"}
      fontSize="xs"
      fontWeight={500}
    >
      <Icon as={added ? Plus : Minus} boxSize="10px" />
      {name}
    </Box>
  );
};

export interface ChangeCardProps {
  change: EntitlementChange;
}

const ChangeCard = ({ change }: ChangeCardProps) => {
  const by = attribution(change);
  return (
    <Box
      p="md"
      borderRadius="component.2xl"
      border="1px"
      borderColor="border.default"
      bg="bg.container"
    >
      <HStack justify="space-between" align="flex-start" spacing="md">
        <VStack align="flex-start" spacing="sm" flex="1" minW={0}>
          <Text
            fontSize="smd"
            fontWeight={600}
            color="text.body.primary"
            noOfLines={1}
          >
            {change.accountLabel}
          </Text>
          <Wrap spacing="1xs">
            {change.added.map((name) => (
              <WrapItem key={`add-${name}`}>
                <EntTag name={name} direction="added" />
              </WrapItem>
            ))}
            {change.removed.map((name) => (
              <WrapItem key={`rm-${name}`}>
                <EntTag name={name} direction="removed" />
              </WrapItem>
            ))}
          </Wrap>
          {by && (
            <Text fontSize="xs" color="text.body.tertiary">
              {by}
            </Text>
          )}
        </VStack>

        {change.applied && (
          <Text
            fontSize="xs"
            fontWeight={600}
            color="success.700"
            bg="bg.success"
            px="sm"
            py="2px"
            borderRadius="component.md"
            flexShrink={0}
          >
            Applied
          </Text>
        )}
      </HStack>
    </Box>
  );
};

export default ChangeCard;
